import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import TableToolbar from './TableToolbar';
import Pagination from './Pagination';
import { t } from '../../i18n/i18n';
import './DataTable.scss';

const cellText = (row, col) => {
  const raw = col.value ? col.value(row) : row[col.key];
  return raw == null ? '' : String(raw);
};

const sortValue = (row, col) => (col.sortValue ? col.sortValue(row) : row[col.key]);

/* The two heights `maxVisibleRows` counts in, from DataTable.scss. The cap ends
   on half a row rather than a whole one: the scrollbar is 6px of near-black and
   easy to miss, where a row cut through the middle is unmistakably "there is
   more below". */
const ROW_H = 38;
const HEAD_H = 28;

const compare = (a, b) => {
  const aNull = a == null || (typeof a === 'number' && Number.isNaN(a));
  const bNull = b == null || (typeof b === 'number' && Number.isNaN(b));
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

/**
 * columns: [{
 *   key, header,
 *   width?      grid weight (fr), matching the design's per-column ratios
 *   align?      'left' | 'right'
 *   tone?       (row) => 'up' | 'down' | 'muted' | 'primary'
 *   value?      (row) => primitive        // text + search + default sort source
 *   render?     (row) => ReactNode        // full cell control (chips, flags, links)
 *   sortValue?  (row) => primitive
 *   sortable?   boolean                   // header stays plain unless opted in
 *   searchable? boolean                   // default true
 * }]
 *
 * Pass `total` to switch to server-side mode: `rows` is treated as the current
 * page already filtered and sorted, and search / sort / slice are skipped.
 * Such a table wires `page` / `onPageChange` for the pager and `onSearch` for
 * the search box — the input stays owned here, the query does not.
 */
export default function DataTable({
  title,
  count,
  icon,
  subtitle,
  columns,
  rows,
  getRowId = (row, i) => row.id ?? i,
  minWidth = 1080,
  className = '',

  tabs,
  searchPlaceholder,
  // Server-side tables take the search term instead of filtering in here.
  onSearch,
  onFilters,
  onExport,
  actions,
  filter,
  filters,
  collapsedRows,
  isExpanded,

  selectedId,
  onRowClick,

  // One row at a time opens a full-width panel beneath itself — the payout
  // history's journey strip. The open row is the caller's state, since the
  // panel's content usually needs its own fetch.
  expandedId,
  renderExpanded,

  pageSize = 10,
  page: pageProp,
  onPageChange,
  total: totalProp,

  /**
   * Sizes offered in the pager, e.g. `[10, 25, 50]`. Omitted, the selector is
   * not drawn at all — see the note in Pagination.jsx for why this is opt-in
   * rather than on by default.
   */
  pageSizeOptions,

  /**
   * Rows the body shows before it scrolls rather than keeps growing. Fifty
   * rows is nearly two screens of card, which pushes everything under the
   * table off the page and leaves the pager — the control you need to get back
   * — at the bottom of a long scroll.
   *
   * Defaults to ten wherever the reader chooses the page size, since that is
   * the only way a table ends up with more rows than it was designed around.
   * `null` lets the body grow to whatever it holds, which is what every other
   * table already does.
   */
  maxVisibleRows = pageSizeOptions?.length ? 10 : null,

  loading = false,
  emptyMessage = 'Nothing to show yet',

  // Some tables end in a call-to-action instead of a pager — the Dashboard
  // accounts card (1:8141) is "View All" rather than "Showing 1-n of m".
  pagination = true,
  footer,
}) {
  const serverMode = totalProp != null;

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null); // { key, dir: 'asc' | 'desc' }
  const [pageState, setPageState] = useState(1);

  const page = pageProp ?? pageState;
  const setPage = onPageChange ?? setPageState;

  /* The chosen size is held here, not only announced to the caller: in client
     mode nobody else is listening, and in server mode the slice maths, the page
     count and the "showing x-y of z" line all have to agree with the selector
     from the moment it changes rather than one response later.

     Seeded from the prop and re-seeded when it changes, so a server that
     answers with a different `page_size` than was asked for still wins. */
  const [sizeState, setSizeState] = useState(pageSize);
  useEffect(() => setSizeState(pageSize), [pageSize]);
  const size = sizeState;

  const changePageSize = (next) => {
    setSizeState(next);
    // Back to page one: whatever was on page 7 of 22 is somewhere else now, and
    // page 7 may not exist at the new size.
    setPage(1, next);
  };

  const template = useMemo(
    () =>
      columns
        .map((c) => {
          const min = c.minWidth
            ? typeof c.minWidth === 'number'
              ? `${c.minWidth}px`
              : c.minWidth
            : '0';
          return `minmax(${min}, ${c.width ?? 1}fr)`;
        })
        .join(' '),
    [columns]
  );

  const filtered = useMemo(() => {
    if (serverMode || !search.trim()) return rows;
    const needle = search.trim().toLowerCase();
    const cols = columns.filter((c) => c.searchable !== false);
    return rows.filter((row) =>
      cols.some((col) => cellText(row, col).toLowerCase().includes(needle))
    );
  }, [rows, columns, search, serverMode]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => compare(sortValue(a, col), sortValue(b, col)) * dir);
  }, [filtered, columns, sort]);

  const total = totalProp ?? sorted.length;
  const pageCount = Math.max(Math.ceil(total / size), 1);
  const current = Math.min(page, pageCount);
  const start = (current - 1) * size;
  const pageRows = serverMode ? sorted : sorted.slice(start, start + size);
  const hasMoreHidden = Boolean(collapsedRows && !isExpanded && sorted.length > collapsedRows);

  /* A `collapsedRows` table animates between two heights, which needs a real
     number to transition to — `max-height` cannot animate to `none`. That cap
     used to be a flat 500px, about twelve rows, so once the pager could ask
     for fifty the rest of the page was simply cut off behind `overflow:
     hidden`. It is measured now, and what is rendered decides it: the rows on
     the page, or the skeletons standing in for them.

     The generous per-row estimate is what the first frame uses, since nothing
     can be measured before it is laid out. It only ever over-shoots, so the
     worst it can do is leave slack under the last row for one frame — never
     hide one. */
  const bodyRef = useRef(null);
  const [bodyMax, setBodyMax] = useState(null);
  const renderedRows = loading ? size : pageRows.length;

  /* Past the cap the body scrolls in place instead of growing the card. The
     collapsed state is never scrolled: it is already a 78px window on the same
     rows, and two nested scrollers over one list would fight each other. */
  const scrolls =
    maxVisibleRows != null && renderedRows > maxVisibleRows && !(collapsedRows && !isExpanded);

  useLayoutEffect(() => {
    if (!collapsedRows || !isExpanded) {
      // Collapsed is the class's own 78px, and a table without `collapsedRows`
      // is never capped at all.
      setBodyMax(null);
      return;
    }
    setBodyMax(bodyRef.current?.scrollHeight ?? null);
  }, [collapsedRows, isExpanded, renderedRows, pageRows]);

  /*
   * The selected row's highlight, travelling — the sidebar's `.nav-fill`
   * (Sidebar.jsx) brought to the table: one marker that slides to the row
   * picked, so the eye follows the selection instead of re-finding it. Same
   * look as before (the raised fill and the 2px accent); only its movement is
   * new.
   *
   * It slides only when the selection moves within the same set of rows. A new
   * page, a filter or a refresh changes the rows themselves — easing across a
   * list that has just been replaced reads as drift — so the marker is placed
   * outright there, and earns its transition back on the next pick.
   *
   * `offsetTop`, not rects: rows fan in with a transform on arrival, and a
   * rect measured mid-animation would put the marker off by that offset.
   */
  const rowKeys = loading ? '' : pageRows.map((row, i) => getRowId(row, i)).join('|');
  const [fill, setFill] = useState(null);
  const [fillSmooth, setFillSmooth] = useState(false);
  const fillRows = useRef(null);
  const fillPlaced = useRef(false);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const row = selectedId == null ? null : body?.querySelector('.data-table__row.is-selected');
    if (!row) {
      fillPlaced.current = false;
      setFill(null);
      return;
    }
    const sameRows = fillRows.current === rowKeys;
    fillRows.current = rowKeys;
    setFillSmooth(sameRows && fillPlaced.current);
    fillPlaced.current = true;
    setFill({ top: row.offsetTop, height: row.offsetHeight });
  }, [selectedId, rowKeys, expandedId]);

  const onSearchChange = (next) => {
    setSearch(next);
    setPage(1);
    onSearch?.(next);
  };

  const toggleSort = (col) => () => {
    if (!col.sortable) return;
    setSort((prev) =>
      prev?.key === col.key
        ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: 'asc' }
    );
    setPage(1);
  };

  return (
    <section className={`data-table ${className}`.trim()}>
      <TableToolbar
        title={title}
        // `false` drops the "(n)" suffix for a title whose design carries none.
        count={count === false ? null : count ?? total}
        icon={icon}
        tabs={tabs}
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        onFilters={onFilters}
        onExport={onExport}
        actions={actions}
        filter={filter}
        filters={filters}
      />

      {subtitle && <p className="data-table__subtitle">{t(subtitle)}</p>}

      <div
        className={`data-table__scroll${scrolls ? ' is-scrollable' : ''}`}
        style={scrolls ? { maxHeight: HEAD_H + maxVisibleRows * ROW_H + ROW_H / 2 } : undefined}
      >
        <div className="data-table__grid" style={{ minWidth }}>
          <div className="data-table__head" style={{ gridTemplateColumns: template }} role="row">
            {columns.map((col) => (
              <div
                key={col.key}
                role="columnheader"
                aria-sort={
                  col.sortable
                    ? sort?.key === col.key
                      ? sort.dir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                    : undefined
                }
                className={[
                  'data-table__th',
                  col.align === 'right' && 'is-right',
                  col.className,
                  col.sortable && 'is-sortable',
                  sort?.key === col.key && `is-sorted-${sort.dir}`,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={toggleSort(col)}
              >
                {t(col.header)}
              </div>
            ))}
          </div>

          <div
            ref={bodyRef}
            className={[
              'data-table__body-wrapper',
              collapsedRows && !isExpanded && 'is-collapsed',
              collapsedRows && isExpanded && 'is-expanded',
              fill && 'has-fill',
            ].filter(Boolean).join(' ')}
            style={
              collapsedRows && isExpanded
                ? { maxHeight: bodyMax ?? renderedRows * 56 + 48 }
                : undefined
            }
          >
            {loading &&
              Array.from({ length: size }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="data-table__row is-loading"
                  style={{ gridTemplateColumns: template }}
                >
                  {columns.map((col) => (
                    <div key={col.key} className="data-table__td">
                      <span className="data-table__skeleton" />
                    </div>
                  ))}
                </div>
              ))}

            {!loading &&
              pageRows.map((row, i) => {
                const id = getRowId(row, i);
                return (
                  <Fragment key={id}>
                  <div
                    role="row"
                    className={[
                      'data-table__row',
                      id === selectedId && 'is-selected',
                      onRowClick && 'is-clickable',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{ gridTemplateColumns: template }}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => {
                      const tone = col.tone?.(row);
                      return (
                        <div
                          key={col.key}
                          role="cell"
                          className={[
                            'data-table__td',
                            col.align === 'right' && 'is-right',
                            col.className,
                            tone && `is-${tone}`,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {col.render ? col.render(row) : cellText(row, col)}
                        </div>
                      );
                    })}
                  </div>

                  {renderExpanded && id === expandedId && (
                    <div className="data-table__expanded">{renderExpanded(row)}</div>
                  )}
                  </Fragment>
                );
              })}

            {!loading && pageRows.length === 0 && (
              <p className="data-table__empty">{t(emptyMessage)}</p>
            )}

            {/* Last, so the rows' nth-child stagger is unchanged; drawn under
                them all the same (z-index), carrying the selected row's look. */}
            {fill && (
              <span
                className={`data-table__fill${fillSmooth ? ' is-smooth' : ''}`}
                style={{ transform: `translate3d(0, ${fill.top}px, 0)`, height: `${fill.height}px` }}
                aria-hidden="true"
              />
            )}
          </div>
        </div>
        <div className={`data-table__fade-overlay ${hasMoreHidden ? 'is-visible' : ''}`} />
      </div>

      {pagination && (
        <Pagination
          page={current}
          pageCount={pageCount}
          from={total === 0 ? 0 : start + 1}
          to={Math.min(start + size, total)}
          total={total}
          onChange={(nextPage) => setPage(nextPage, size)}
          pageSize={size}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={changePageSize}
        />
      )}

      {footer}
    </section>
  );
}
